import React, { useRef, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import { getFirebaseToken } from '../../utils/getFirebaseToken';
import { transactionTemplatesService } from '../../services/transactionTemplates.service';
import { ROW_HEIGHT } from './TransactionChecklistForms';
import './TransactionChecklistForms.css';

/**
 * Placeholder rows for a folder's UPLOAD checklist items in Transaction
 * Documents. A row is only an entry until the agent uploads its file into this
 * folder; the item is then marked Uploaded and shows the file name.
 */
const TransactionChecklistUploads = ({ transaction, folder, items, onUploaded }) => {
  const fileInputRef = useRef(null);
  // Row whose Upload opened the file chooser. Kept in a ref, not state: closing the chooser
  // without a file fires no event, so nothing may look "uploading" until a file is picked.
  const pickingItemRef = useRef(null);
  // Rows uploading right now — each row uploads on its own, so others stay usable.
  const [uploadingIds, setUploadingIds] = useState(() => new Set());
  const [error, setError] = useState('');

  if (items.length === 0) return null;

  const startUpload = (item) => {
    setError('');
    pickingItemRef.current = item;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const item = pickingItemRef.current;
    pickingItemRef.current = null;
    if (!file || !item) return;
    setUploadingIds((prev) => new Set(prev).add(item.id));
    try {
      const token = await getFirebaseToken();
      const formData = new FormData();
      formData.append('file', file, file.name);
      formData.append('folder', folder);
      // Raw axios (not apiClient) so the browser sets the multipart boundary — same as DisclosureTab.
      const response = await axios.post(API_ENDPOINTS.uploadTransactionFile(transaction.id), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await transactionTemplatesService.markChecklistItemUploaded(transaction.id, item.id, {
        documentId: response.data?.documentId,
        fileName: response.data?.fileName || file.name,
      });
      onUploaded();
    } catch (err) {
      console.error('[TransactionChecklistUploads] Upload failed', item.name, err);
      setError(`Couldn't upload ${item.name}. Please try again.`);
    } finally {
      setUploadingIds((prev) => { const next = new Set(prev); next.delete(item.id); return next; });
    }
  };

  const doneCount = items.filter((i) => i.status !== 'PENDING').length;
  // Once uploaded, the file itself shows in the folder's document list below, so only
  // items still waiting for a file keep a placeholder row (no duplicate NHD row).
  const pendingItems = items.filter((i) => i.status === 'PENDING');
  if (pendingItems.length === 0) return null;

  return (
    <section className="txn-checklist-block" aria-label="Required documents">
      <div className="txn-checklist-header">
        <div className="txn-checklist-heading">
          <h3 className="txn-checklist-title">Required documents</h3>
          <span className="txn-checklist-progress">{doneCount} of {items.length} uploaded</span>
        </div>
      </div>
      {error && <p className="txn-checklist-error">{error}</p>}
      <input ref={fileInputRef} type="file" hidden onChange={handleFileChange} />
      {/* Same card + row layout as the document lists (txn-doc-dyn-*). */}
      <div className="txn-doc-dyn-body">
        <div className="txn-doc-dyn-rows">
          {pendingItems.map((item, idx) => (
            <div
              key={item.id}
              className={`txn-doc-dyn-row txn-checklist-row-card txn-upload-placeholder is-locked${idx === pendingItems.length - 1 ? ' txn-doc-dyn-row-last' : ''}`}
              style={{ height: ROW_HEIGHT }}
            >
              <div className="txn-doc-dyn-icon txn-upload-placeholder-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              </div>
              <div className="txn-doc-dyn-main">
                <div className="txn-doc-name">{item.name}</div>
                <div className="txn-doc-type">No file yet · {item.mandatory ? 'Mandatory' : 'Optional'}</div>
              </div>
              <div className="txn-doc-status txn-upload-placeholder-pill">Pending upload</div>
              <button
                type="button"
                className="txn-checklist-add-btn"
                onClick={() => startUpload(item)}
                disabled={uploadingIds.has(item.id)}
              >
                {uploadingIds.has(item.id) ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TransactionChecklistUploads;
